'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useImage } from '@hooks/useImage';
import { Listing, School } from 'types/collections';

import { ColumnContainer, Label, SectionBigGap } from '@components/Common';
import { ShiftInfoCardListing } from '@components/ShiftInfoCards';
import { TextGroup } from '@components/TextGroups';

import { SchoolHeader } from '.';
import { ImageContainer, RadioGroup, StyledImage } from './styles';

export interface CompletedSchoolListingProps {
  listing: Listing;
  schoolProfile: School | null;
  selectedShiftId?: string | null;
  setSelectedShiftId?: (shiftId: string | null) => void;
  isTeacher?: boolean;
}

/**
 * Completed School Listing Display component
 */
export const CompletedSchoolListing: React.FC<CompletedSchoolListingProps> = ({
  listing,
  schoolProfile,
  selectedShiftId,
  setSelectedShiftId,
  isTeacher,
}) => {
  const { t } = useTranslation('common');
  const { imageUrl, imageLoading } = useImage(schoolProfile?.photo_path);

  //TODO: change it on shifts going from the join tabel of reactions and not reacted on status,
  // or if the shift_id/teacher_id entry is not yet in the join table
  const shifts = listing?.shifts || [];

  return (
    <>
      <ImageContainer>
        {!imageLoading && imageUrl && <StyledImage src={imageUrl} alt={'School photo'} />}
      </ImageContainer>
      <SectionBigGap>
        <SchoolHeader schoolProfile={schoolProfile || null} />
        {shifts?.length > 0 && isTeacher && (
          <ColumnContainer>
            <Label>{t('listingAvailability')}</Label>
            <RadioGroup>
              {shifts.map((shift) => (
                <ShiftInfoCardListing
                  key={shift.id}
                  shiftId={shift.id}
                  group={shift.group || ''}
                  date={shift.shift_date}
                  timeFrom={shift.time_from}
                  timeUntil={shift.time_till}
                  isSelected={selectedShiftId === shift.id}
                  onSelect={setSelectedShiftId || (() => {})}
                />
              ))}
            </RadioGroup>
          </ColumnContainer>
        )}

        <TextGroup header={t('aboutUs')} paragraph={listing.intro} listing={true} />
        <TextGroup header={t('schoolListing.function')} paragraph={listing.role} listing={true} />
        <TextGroup
          header={t('schoolListing.regulation')}
          paragraph={listing.rules}
          listing={true}
        />
        <TextGroup
          header={t('schoolListing.supplies')}
          paragraph={listing.supplies}
          listing={true}
        />
      </SectionBigGap>
    </>
  );
};
